import axios from "axios";
import { Alert, Box, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { getInputLabelProps, getInputProps } from "../../common/appStyles";
import localize from "../../common/localize";
import { LoadingButton } from "@mui/lab";
import { ArrowForward, Search } from "@mui/icons-material";
import useGlobalContext from "../../hooks/useGlobalContext";
import TableComponent from "./TableComponent";
import { generateNoDataValues } from "../../common/utils";
import ModalBackground from "./ModalBackground";
import ActionButtons from "./ActionButtons";
import ValueOrNoDataLabel from "./ValueOrNoDataLabel";
import Combobox from "./Combobox";

export default function SupplySearch({
  setResultList,
  showSearch,
  setShowSearch,
  singleSearch,
  warehouse,
  setUsedWarehouse,
  blockWarehouse,
  is_in_movement,
}) {
  const {
    SUPPLY_API,
    WAREHOUSE_ENDPOINT,
    config,
    getHeadersWithCorrespondingPagination,
  } = useGlobalContext();
  const searchTypes = [
    {
      Id: "Code",
      Name: localize.code,
    },
    {
      Id: "Name",
      Name: localize.name,
    },
    {
      Id: "Tags",
      Name: localize.tags,
    },
  ];
  const [foundItems, setFoundItems] = useState([]);
  const [errorText, setErrorText] = useState(undefined);
  const [searchType, setSearchType] = useState(searchTypes[0]);
  const [searchText, setSearchText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState(
    warehouse ?? { Id: 0, Name: localize.main_warehouse }
  );

  const warehouseOptions = [
    {
      Id: 0,
      Name: localize.main_warehouse,
    },
    warehouse,
  ];

  useEffect(() => {
    setUsedWarehouse && setUsedWarehouse(selectedWarehouse);
  }, [selectedWarehouse]);

  const handleSearchTypeChange = (value) => {
    if (!value) {
      setSearchType({
        ...searchTypes[0],
      });
      return;
    }

    setSearchType(value);
  };

  const handleChange = (e) => {
    setSearchText(e.target.value);
  };

  const search = async () => {
    try {
      setErrorText(undefined);
      setIsLoading(true);

      if (selectedWarehouse?.Id > 0 && !is_in_movement) {
        getSuppliesFromWarehouse(searchType, searchText);
        return;
      }

      const items = await getSuppliesInformation(searchType, searchText);

      if (items?.length > 0) {
        const toAdd = [];

        items.forEach((foundItem) => {
          const index = foundItems.findIndex((x) => x.Id === foundItem.Id);
          if (index < 0) {
            toAdd.push(foundItem);
          }
        });

        if (toAdd.length === 0) {
          setErrorText(localize.error_supply_already_added);
          setIsLoading(false);
          return;
        }

        setFoundItems([...toAdd, ...foundItems]);
      } else {
        setErrorText(localize.error_no_supply_found);
      }

      setIsLoading(false);
    } catch {
      setErrorText(localize.error_unexpected);
      setIsLoading(false);
    }
  };

  const getSuppliesFromWarehouse = async (searchType, searchValue) => {
    try {
      setErrorText(undefined);
      setIsLoading(true);

      const items = await axios.get(
        `${WAREHOUSE_ENDPOINT}/${warehouse?.Id}/supplies`,
        getHeadersWithCorrespondingPagination(config, -1, [
          {
            Name: searchType.Id,
            FriendlyName: searchType.Name,
            FilterValue: { Value: searchValue },
            Value: searchValue,
          },
        ])
      );

      if (items.data?.Data) {
        setFoundItems([
          ...items.data?.Data?.map((item) => ({
            Id: item.SupplyElement?.Id,
            Name: item.SupplyElement?.Name,
            Code: item.SupplyElement?.Code,
            Quantity: item.Quantity,
          })),
        ]);
      }

      setIsLoading(false);
    } catch (error) {
      console.error("Error loading warehouse supplies: ", error);
      setIsLoading(false);
    }
  };

  const getSuppliesInformation = async (searchType, searchValue) => {
    try {
      const supplies = await axios.get(
        SUPPLY_API,
        getHeadersWithCorrespondingPagination(config, -1, [
          {
            Name: searchType.Id,
            FriendlyName: searchType.Name,
            FilterValue: { Value: searchValue },
            Value: searchValue,
          },
        ])
      );

      if (supplies?.data?.Data) {
        setSearchText("");
        return supplies.data.Data;
      }

      setErrorText(localize.error_no_supply_found);
      return [];
    } catch {
      setErrorText(localize.error_unexpected);
      return [];
    }
  };

  const selectItem = (item) => {
    setResultList([item]);
    setShowSearch(false);
  };

  const handleWarehouseSelection = (value) => {
    setFoundItems([]);

    if (!value) {
      setSelectedWarehouse(warehouseOptions[0]);
      return;
    }

    setSelectedWarehouse(value);
  };

  const keyUp = (e) => {
    if (e.key === "Enter") {
      search();
    }
  };

  const getComponents = () => {
    return (
      <Box className="flex flex-col gap-2 my-2" style={{ width: "100%", maxWidth: "800px" }}>
        {warehouse && warehouse.Id > 0 && (
          <Combobox
            id="Warehouse"
            handleValueChange={handleWarehouseSelection}
            fieldValue={selectedWarehouse}
            fieldOptions={warehouseOptions ?? []}
            fieldLabel={localize.warehouse}
            disabled={blockWarehouse}
          />
        )}

        <Alert
          className="w-full"
          severity={
            selectedWarehouse && selectedWarehouse?.Id === warehouse?.Id
              ? "warning"
              : "info"
          }
        >
          <label className="text-xs">
            {localize.warning_warehouse_usage.replaceAll(
              "%s1",
              selectedWarehouse.Name
            )}
          </label>
        </Alert>

        <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2, mt: 2 }}>
          <Combobox
            id="SearchType"
            handleValueChange={handleSearchTypeChange}
            fieldValue={searchType}
            fieldOptions={searchTypes ?? []}
            fieldLabel={localize.search_type}
          />

          <TextField
            InputLabelProps={getInputLabelProps()}
            inputProps={getInputProps()}
            value={searchText}
            onChange={handleChange}
            size="small"
            id="SearchText"
            name="SearchText"
            label={searchType?.Name}
            variant="outlined"
            fullWidth
            onKeyUp={keyUp}
          />

          <LoadingButton
            loading={isLoading}
            sx={{
              fontSize: "0.8em",
              backgroundColor: "#10366E",
              width: { xs: "100%", sm: "180px" },
              height: "35px",
            }}
            size="small"
            variant="contained"
            type="button"
            onClick={() => {
              search();
            }}
            disabled={!searchText || searchText?.length === 0}
          >
            <span className="mr-2">{localize.search}</span>
            <Search className="" style={{ marginLeft: "2px" }} />
          </LoadingButton>
        </Box>

        {errorText && (
          <Alert className="w-full" severity="error">
            <label className="text-xs">{errorText}</label>
          </Alert>
        )}

        <TableComponent
          noPadding
          firstColumnTitle={localize.actions}
          columnTitles={[localize.code, localize.name, localize.quantity]}
          fixedSizeInPx={300}
          results={
            foundItems && foundItems.length > 0
              ? foundItems.map((item) => ({
                  key: item.Id,
                  data: [
                    <ActionButtons
                      hideEditButton
                      key={`${item.Id}_action`}
                      buttons={
                        singleSearch
                          ? [
                              {
                                name: localize.select,
                                icon: (
                                  <ArrowForward
                                    style={{
                                      color: "white",
                                      width: "15px",
                                      height: "15px",
                                    }}
                                  />
                                ),
                                action: () => {
                                  selectItem(item);
                                },
                              },
                            ]
                          : undefined
                      }
                      onclickForDeleteButton={() => {
                        removeEventFromList(item.Id);
                      }}
                    />,
                    <ValueOrNoDataLabel data={item.Code} />,
                    <ValueOrNoDataLabel data={item.Name} />,
                    item.Quantity ?? 0,
                  ],
                }))
              : [generateNoDataValues(4)]
          }
        />
      </Box>
    );
  };

  const selectCurrentSupplies = () => {
    setResultList([...foundItems]);
    setShowSearch(false);
  };

  const removeEventFromList = (supplyId) => {
    const index = foundItems.findIndex((x) => x.Id === supplyId);
    if (index >= 0) {
      foundItems.splice(index, 1);
      setFoundItems([...foundItems]);
    }
  };

  return (
    <ModalBackground
      showModal={showSearch}
      Component={getComponents()}
      okButtonText={localize.select}
      hideSubmitButton={singleSearch}
      onClickToSubmit={() => {
        selectCurrentSupplies();
      }}
      validators={!foundItems || foundItems?.length === 0}
      onClickToCancel={() => {
        setShowSearch(false);
      }}
      cancelButtonText={localize.close}
      title={localize.supply_search}
    />
  );
}
